"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Engineering() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: dividerRef,
    isVisible: dividerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2793" id="precision-aquatic-engineering" className="relative w-full min-h-screen overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute top-6 left-6 right-6 md:top-8 md:left-12 md:right-12 z-30 flex justify-between items-center pointer-events-none">
        <div className="flex items-center gap-3">
          <span className="inline-block size-1 bg-primary" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/70">
            ROCKET CITY WATER WIZARDS &middot; SDVOSB
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-white/70">
          <span>HUNTSVILLE, AL &middot; 34.7304&deg; N, 86.5861&deg; W</span>
          <span className="size-1 bg-primary" />
        </div>
      </div>

      <div className="relative flex flex-col lg:flex-row min-h-screen">
        {}
        <div ref={leftRef} className={`relative w-full lg:w-1/2 min-h-[60vh] lg:min-h-screen bg-background overflow-hidden transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_installation_site.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_installation_site.png-wvc-sizes" alt="Drone site survey of a new pool installation in Huntsville, Alabama" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover saturate-50 opacity-80" src="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/drone-mapping.png" />
          <div className="absolute inset-0 bg-background/40" />
        </div>

        {}
        <div ref={rightRef} className={`relative w-full lg:w-1/2 min-h-[60vh] lg:min-h-screen bg-secondary overflow-hidden transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/crystal_clear_water.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/crystal_clear_water.png-wvc-sizes" alt="Finished custom pool with spa and water feature in a North Alabama backyard" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" src="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/RCWW-luxury-custom-pool-backyard-huntsville-al.png" />
          <div className="absolute inset-0 bg-secondary/20" />
        </div>

        {}
        <div className="absolute inset-0 z-[15] pointer-events-none" style={{
        background: "linear-gradient(to top, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.50) 45%, rgba(0,0,0,0.28) 100%)"
      }} />
        <div className="absolute inset-0 z-[15] pointer-events-none" style={{
        background: "radial-gradient(ellipse 70% 55% at 50% 46%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.30) 55%, rgba(0,0,0,0) 100%)"
      }} />

        {}
        <div ref={dividerRef} className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px overflow-hidden pointer-events-none z-[16]">
          <div className={`w-px bg-primary transition-all duration-[1800ms] ease-out ${dividerVisible ? "h-full" : "h-0"}`} />
        </div>

        {}
        <div className="hidden lg:block absolute bottom-12 left-8 lg:left-12 z-20 pointer-events-none">
          <div className="flex items-center gap-3">
            <span className="block h-px w-8 bg-white/50" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              Phase 01 // Site Survey
            </span>
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-12 right-8 lg:right-12 z-20 text-right pointer-events-none">
          <div className="flex items-center justify-end gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              Phase 14 // Swim Day
            </span>
            <span className="block h-px w-8 bg-white/50" />
          </div>
        </div>

        {}
        <div ref={headlineRef} className={`absolute inset-0 flex items-center justify-center px-6 lg:px-12 z-20 pointer-events-none transition-all duration-1000 ease-out delay-300 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="w-full max-w-4xl text-center pointer-events-auto -translate-y-[6vh]">
            <div className="inline-flex items-center gap-2 mb-6 font-mono text-[11px] md:text-xs uppercase tracking-[0.28em] text-primary">
              <span className="block h-px w-6 bg-primary" />
              Engineered in the Tennessee Valley
              <span className="block h-px w-6 bg-primary" />
            </div>

            <h1 className="font-sans font-bold leading-[1.04] tracking-tighter text-[34px] sm:text-5xl md:text-6xl lg:text-7xl bg-clip-text text-transparent" style={{
            backgroundImage: "linear-gradient(105deg, #FFFFFF 0%, #FFFFFF 38%, #BDE9F5 62%, var(--chart-1) 100%)",
            filter: "drop-shadow(0 2px 28px rgba(0,0,0,0.85))"
          }}>
              Precision aquatic engineering for{" "}
              <em className="italic font-normal">modern</em> living.
            </h1>

            <p className="mt-6 lg:mt-8 mx-auto max-w-[34ch] text-[17px] md:text-xl font-sans text-white/90 leading-relaxed" style={{
            textShadow: "0 2px 20px rgba(0,0,0,0.9)"
          }}>
              They send a guy with a tape measure. We send a drone, an engineer,
              and a plan.
            </p>

            <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <Button asChild size="lg" className="group px-8 py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-secondary transition-all rounded-md shadow-lg">
                <Link to="/InstallationsPage">
                  <span>Start Your Build</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="group px-8 py-6 text-base font-medium border-white/40 bg-white/5 backdrop-blur-sm hover:bg-white/15 text-white transition-all rounded-md">
                <Link to="/PreviousWorkPage">
                  <span>See How We Build</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1 opacity-70" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="lg:hidden absolute left-1/2 top-[60vh] -translate-x-1/2 -translate-y-1/2 w-16 h-px bg-primary z-[16]" />
    </section>;
}